import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Menu,
  Tooltip,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { performanceService } from '../../services/performanceService';
import { hasPermission, isAdmin } from '../../services/authService';

const ExecutionsTab = ({ onViewExecution, selectedProjectId }) => {
  const [executions, setExecutions] = useState([]);
  const [filteredExecutions, setFilteredExecutions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [sortField, setSortField] = useState('executionDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    projectId: '',
    release: '',
    build: '',
    userLoad: '',
    executionDate: dayjs(),
    cpus: '',
    memory: '',
    instanceName: '',
    datadogLink: ''
  });

  useEffect(() => {
    fetchExecutions();
    fetchProjects();
  }, []);

  useEffect(() => {
    // Apply project filter when selectedProjectId changes
    if (selectedProjectId) {
      setProjectFilter(selectedProjectId);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    // Filter executions based on search query and project filter
    let filtered = executions;

    // Apply project filter first
    if (projectFilter) {
      filtered = filtered.filter(execution =>
        execution.projectId?._id === projectFilter
      );
    }

    // Then apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(execution =>
        (execution.projectId?.projectName && execution.projectId.projectName.toLowerCase().includes(query)) ||
        (execution.release && execution.release.toLowerCase().includes(query)) ||
        (execution.build && execution.build.toLowerCase().includes(query)) ||
        (execution.instanceName && execution.instanceName.toLowerCase().includes(query)) ||
        (execution.status && execution.status.toLowerCase().includes(query)) ||
        (execution.createdBy?.name && execution.createdBy.name.toLowerCase().includes(query)) ||
        (execution.createdBy?.email && execution.createdBy.email.toLowerCase().includes(query))
      );
    }

    setFilteredExecutions(filtered);
  }, [searchQuery, executions, projectFilter]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const response = await performanceService.getExecutions();
      setExecutions(response.data);
      setFilteredExecutions(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch executions');
      console.error('Error fetching executions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await performanceService.getProjects();
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      projectId: '',
      release: '',
      build: '',
      userLoad: '',
      executionDate: dayjs(),
      cpus: '',
      memory: '',
      instanceName: '',
      datadogLink: ''
    });
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      executionDate: date
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setError('');
    } else {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedFile) {
        setError('Please select a CSV file');
        return;
      }

      setUploading(true);
      const formDataToSend = new FormData();
      
      // Append form fields
      Object.keys(formData).forEach(key => {
        if (key === 'executionDate') {
          formDataToSend.append(key, formData[key].toISOString());
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append file
      formDataToSend.append('csvFile', selectedFile);

      await performanceService.createExecution(formDataToSend);
      handleCloseDialog();
      fetchExecutions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create execution');
    } finally {
      setUploading(false);
    }
  };

  const handleMenuOpen = (event, execution) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedExecution(execution);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedExecution(null);
  };

  const handleDeleteClick = () => {
    // Don't clear the selectedExecution when closing the menu
    setMenuAnchorEl(null);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    // Don't clear selectedExecution here, only after successful deletion
  };

  const handleDeleteExecution = async () => {
    console.log('handleDeleteExecution called');
    if (!selectedExecution) {
      console.error('No execution selected');
      return;
    }

    try {
      setStatusUpdating(true);
      console.log(`Deleting execution ${selectedExecution._id}`);
      
      console.log('Calling deleteExecution API...');
      const response = await performanceService.deleteExecution(selectedExecution._id);
      console.log('API response:', response);
      
      // Update the local state by removing the deleted execution
      setExecutions(executions.filter(exec => exec._id !== selectedExecution._id));
      
      console.log('Closing dialog and clearing selected execution');
      setConfirmDialogOpen(false);
      setSelectedExecution(null);
    } catch (err) {
      console.error('Error deleting execution:', err);
      setError(`Failed to delete execution: ${err.message}`);
    } finally {
      setStatusUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'failed':
        return 'error';
      case 'ignored':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as the sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort executions based on sortField and sortDirection
  const sortedExecutions = [...filteredExecutions].sort((a, b) => {
    // Handle nested fields like projectId.projectName
    if (sortField === 'projectId') {
      const aValue = a.projectId?.projectName || '';
      const bValue = b.projectId?.projectName || '';
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Handle numeric fields
    if (sortField === 'userLoad' || sortField === 'cpus') {
      return sortDirection === 'asc' 
        ? a[sortField] - b[sortField]
        : b[sortField] - a[sortField];
    }
    
    // Handle date fields
    if (sortField === 'executionDate') {
      return sortDirection === 'asc' 
        ? new Date(a[sortField]) - new Date(b[sortField])
        : new Date(b[sortField]) - new Date(a[sortField]);
    }
    
    // Default string comparison for other fields
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    
    return sortDirection === 'asc' 
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  if (loading) {
    return <Typography>Loading executions...</Typography>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {projects.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Please create a project first before creating executions.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Header with Search and Create Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              placeholder="Search executions..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {projectFilter && (
              <Chip
                label={`Project: ${projects.find(p => p._id === projectFilter)?.projectName || 'Unknown'}`}
                onDelete={() => setProjectFilter('')}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2
                }}
              />
            )}
          </Box>
          {isAdmin() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              disabled={projects.length === 0}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3
              }}
            >
              Create Execution
            </Button>
          )}
        </Box>

        {/* Modern Table */}
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'grey.50',
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    py: 2
                  }
                }}
              >
                <TableCell
                  onClick={() => handleSort('projectId')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Project
                    {sortField === 'projectId' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('release')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Release
                    {sortField === 'release' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('build')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Build
                    {sortField === 'build' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('userLoad')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    User Load
                    {sortField === 'userLoad' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('executionDate')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Execution Date
                    {sortField === 'executionDate' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSort('instanceName')}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Instance
                    {sortField === 'instanceName' && (
                      sortDirection === 'asc' ?
                        <ArrowUpwardIcon fontSize="small" /> :
                        <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedExecutions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <AssessmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {searchQuery ? 'No executions found' : 'No executions yet'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery
                        ? 'Try adjusting your search query'
                        : 'Create your first execution to start tracking performance test results'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedExecutions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((execution) => (
                  <TableRow
                    key={execution._id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer'
                      },
                      transition: 'background-color 0.2s',
                      '& .MuiTableCell-root': {
                        py: 2
                      }
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main'
                        }}
                      >
                        {execution.projectId?.projectName || 'Unknown Project'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {execution.release}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {execution.build}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {execution.userLoad}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(execution.executionDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {execution.instanceName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {execution.cpus} CPUs, {execution.memory}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title={execution.status !== 'completed' ? 'Only completed executions can be viewed' : 'View Details'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={execution.status !== 'completed'}
                              onClick={() => onViewExecution && onViewExecution(execution._id)}
                              sx={{
                                bgcolor: execution.status === 'completed' ? 'action.hover' : 'transparent',
                                '&:hover': execution.status === 'completed' ? {
                                  bgcolor: 'primary.main',
                                  color: 'white'
                                } : {},
                                '&.Mui-disabled': {
                                  opacity: 0.3
                                }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {(execution.status === 'completed' || execution.status === 'ignored') && (
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, execution)}
                              sx={{
                                bgcolor: 'action.hover',
                                '&:hover': {
                                  bgcolor: 'grey.300'
                                }
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {sortedExecutions.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedExecutions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          />
        )}

        {/* Create Execution Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>Create New Execution</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Project</InputLabel>
                  <Select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleInputChange}
                    label="Project"
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.projectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  name="release"
                  label="Release"
                  fullWidth
                  variant="outlined"
                  value={formData.release}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  name="build"
                  label="Build"
                  fullWidth
                  variant="outlined"
                  value={formData.build}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  name="userLoad"
                  label="User Load"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formData.userLoad}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <DatePicker
                  label="Execution Date"
                  value={formData.executionDate}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  name="cpus"
                  label="CPUs"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formData.cpus}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Memory</InputLabel>
                  <Select
                    name="memory"
                    value={formData.memory}
                    onChange={handleInputChange}
                    label="Memory"
                  >
                    <MenuItem value="4GB">4GB</MenuItem>
                    <MenuItem value="8GB">8GB</MenuItem>
                    <MenuItem value="16GB">16GB</MenuItem>
                    <MenuItem value="32GB">32GB</MenuItem>
                    <MenuItem value="64GB">64GB</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="instanceName"
                  label="Instance Name"
                  fullWidth
                  variant="outlined"
                  value={formData.instanceName}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="datadogLink"
                  label="Datadog Link (Optional)"
                  fullWidth
                  variant="outlined"
                  value={formData.datadogLink}
                  onChange={handleInputChange}
                  placeholder="https://tr-highq-lab.datadoghq.com/dash/host/..."
                  helperText="Link to Datadog dashboard for infrastructure monitoring during this execution"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ border: '2px dashed #ccc', borderRadius: 1, p: 2, textAlign: 'center' }}>
                  <input
                    accept=".csv"
                    style={{ display: 'none' }}
                    id="csv-file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="csv-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      Select JMeter CSV File
                    </Button>
                  </label>
                  {selectedFile && (
                    <Typography variant="body2" color="primary">
                      Selected: {selectedFile.name}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" color="textSecondary">
                    Upload your JMeter CSV results file
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Uploading and processing CSV file...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={uploading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={
                !formData.projectId || 
                !formData.release || 
                !formData.build || 
                !formData.userLoad || 
                !formData.cpus || 
                !formData.memory || 
                !formData.instanceName || 
                !selectedFile ||
                uploading
              }
            >
              {uploading ? 'Creating...' : 'Create Execution'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {hasPermission('performance:delete') && isAdmin() && (
            <MenuItem onClick={handleDeleteClick}>
              <Box display="flex" alignItems="center">
                <BlockIcon fontSize="small" sx={{ mr: 1 }} />
                Delete Execution
              </Box>
            </MenuItem>
          )}
        </Menu>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Delete Execution
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this execution? This action cannot be undone.
              Deleted executions will be permanently removed from the system and will not appear in charts or trend analysis.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} disabled={statusUpdating}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                console.log('Confirm button clicked');
                try {
                  console.log('Calling handleDeleteExecution...');
                  handleDeleteExecution();
                  console.log('handleDeleteExecution called successfully');
                } catch (error) {
                  console.error('Error calling handleDeleteExecution:', error);
                }
              }} 
              variant="contained" 
              color="error"
              disabled={statusUpdating}
            >
              {statusUpdating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ExecutionsTab;
